import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  createSelector,
  prepareAutoBatched,
} from "@reduxjs/toolkit"
import { searchFulfilled } from "./search-actions"
import { SelectorStateInterface } from "./type-utils"

export type FacetConfig = { type: string; [key: string]: any }
export type Bucket = { name: string; count: number }
type RegistrationEntity = {
  id: string
  config: FacetConfig
  buckets: Bucket[]
}
const registrationsAdapter = createEntityAdapter<RegistrationEntity>()

const initialState = {
  registrations: registrationsAdapter.getInitialState(),
}

type SliceState = typeof initialState
const registrationSelectors = registrationsAdapter.getSelectors<SliceState>(
  (state) => state.registrations,
)

export const facetsSlice = createSlice({
  name: "facets",
  initialState,

  reducers: {
    facetRegistered: {
      reducer(
        state,
        action: PayloadAction<{ id: string; config: FacetConfig }>,
      ) {
        const { id, config } = action.payload
        registrationsAdapter.addOne(state.registrations, {
          id,
          config,
          buckets: [],
        })
      },
      prepare: prepareAutoBatched<{ id: string; config: FacetConfig }>(),
    },

    facetDeregistered(state, action: PayloadAction<string>) {
      const id = action.payload
      registrationsAdapter.removeOne(state.registrations, id)
    },
  },

  extraReducers: (builder) => {
    builder.addCase(searchFulfilled, (state, action) => {
      const { facets } = action.payload
      const updates = Object.entries(facets).map(([facetId, buckets]) => ({
        id: facetId,
        changes: { buckets },
      }))

      registrationsAdapter.updateMany(state.registrations, updates)
    })
  },

  selectors: {
    selectFacetBuckets: createSelector(
      [registrationSelectors.selectById],
      (facet) => facet?.buckets ?? null,
    ),

    selectQueryFacets: createSelector(
      [registrationSelectors.selectAll],
      (allRegistrations) => {
        const entries = allRegistrations.map(
          ({ config, id }) => [id, config] as const,
        )

        return Object.fromEntries(entries)
      },
    ),

    selectFacetRegistered: createSelector(
      [registrationSelectors.selectById],
      (facet) => facet !== undefined,
    ),
  },
})

const { facetRegistered, facetDeregistered } = facetsSlice.actions
export const facetsActions = {
  facetRegistered,
  facetDeregistered,
}

const { selectFacetBuckets, selectQueryFacets, selectFacetRegistered } =
  facetsSlice.selectors

type RootState = SelectorStateInterface<typeof facetsSlice.selectSlice>
const { selectIds: selectFacetIds } =
  registrationsAdapter.getSelectors<RootState>(
    (state) => state.facets.registrations,
  )
export const facetsSelectors = {
  selectFacetBuckets,
  selectQueryFacets,
  selectFacetRegistered,
  selectFacetIds,
}
